import React, { useRef, useEffect, ReactNode, useState } from 'react';
import { toast } from 'react-hot-toast';

import UtilityService from '@/services/util/utility-service';
import { EditIcon } from '@/assets/icons/button';

interface ContentEditableWrapperProps {
  children: ReactNode;
  handleInput?: (content: string) => void;
  handleFocus?: () => void;
  className?: string;
  textStyles?: string;
  [key: string]: any; // To allow additional props
}

const ContentEditableWrapper: React.FC<ContentEditableWrapperProps> = ({
  children,
  handleInput,
  handleFocus,
  className,
  textStyles,
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = String(children);
      checkIfEmpty();
    }
  }, [children]);

  const checkIfEmpty = () => {
    if (contentRef.current) {
      setIsEmpty(contentRef.current.innerText.trim() === '');
    }
  };

  const handleBlur = () => {
    if (contentRef.current) {
      if (contentRef.current.innerText.trim() === '') {
        contentRef.current.focus();
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
        if (handleInput) {
          const cleanedVal = UtilityService.cleanInvalidWhitespace(contentRef.current.innerHTML);
          handleInput(cleanedVal);
          setIsFocused(false);
        }
      }
    }
  };

  const onFocus = () => {
    if (contentRef.current) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(contentRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    if (typeof handleFocus === 'function') {
      handleFocus();
    }
    setIsFocused(true);
  };

  const handleClick = () => {
    if (contentRef.current) {
      contentRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    } else if (e.key === 'Backspace' && isEmpty) {
      e.preventDefault();
      toast.error('Please enter a value before continuing');
    }
  };

  return (
    <span
      className={`flex items-center group w-fit ${className} hover:cursor-text`}
      onClick={handleClick}
      {...props}
    >
      <div
        contentEditable
        ref={contentRef}
        onBlur={handleBlur}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        onInput={checkIfEmpty}
        spellCheck={false}
        className={`w-fit outline-none border-expand border-b-2 ${!isFocused ? 'border-transparent' : isEmpty ? 'border-failure' : 'border-success'} group-hover:border-text_gray_400 ${textStyles}`}
      />
      <EditIcon className='w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2' />
    </span>
  );
};

export default ContentEditableWrapper;
